import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Plus,
  Search,
  Download,
  MoreVertical,
  Edit2,
  PackagePlus,
  History,
  TrendingUp,
  Trash2,
  Package,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import PageHeader from '../components/layout/PageHeader';
import GlassmorphicCard from '../components/common/GlassmorphicCard';
import InputField from '../components/common/InputField';
import SelectField from '../components/common/SelectField';
import Button from '../components/common/Button';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import { Dropdown, DropdownItem } from '../components/common/Dropdown';
import ProductForm from '../components/products/ProductForm';
import { fetchData, downloadFile } from '../api/api';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatDateTime, getCurrentDateISO } from '../utils/date';
import useDebounce from '../utils/debounce';

const ProductsPage = () => {
  const { authToken, selectedShopCode } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);

  // Stock Adjustment State
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [inventoryProductId, setInventoryProductId] = useState(null);
  const [inventoryQuantity, setInventoryQuantity] = useState(0);
  const [inventoryNotes, setInventoryNotes] = useState('');
  const [inventoryType, setInventoryType] = useState('add');

  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // History / Sales Modals
  const [isInventoryHistoryModalOpen, setIsInventoryHistoryModalOpen] = useState(false);
  const [inventoryHistory, setInventoryHistory] = useState([]);
  const [inventoryHistoryLoading, setInventoryHistoryLoading] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);

  const [isProductSalesModalOpen, setIsProductSalesModalOpen] = useState(false);
  const [productSales, setProductSales] = useState([]);
  const [productSalesLoading, setProductSalesLoading] = useState(false);
  const [selectedProductForSales, setSelectedProductForSales] = useState(null);

  const [isSaleDetailsModalOpen, setIsSaleDetailsModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  // Pagination & Export
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const exportFormat = 'csv';

  const debouncedSearchTerm = useDebounce(searchTerm, 600);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const fetchProducts = useCallback(async () => {
    if (!authToken || !selectedShopCode) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      params.append('pageSize', pageSize.toString());
      params.append('pageNumber', currentPage.toString());

      const endpoint = `api/pos/v1/product/search?${params.toString()}`;
      const data = await fetchData(endpoint, 'GET', null, authToken, selectedShopCode);

      setProducts(data.data?.data || []);
      setTotalPages(data.data?.last_page || 1);
      setTotalItems(data.data?.total || 0);
    } catch (err) {
      showError('Failed to fetch products list.');
      setProducts([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedShopCode, debouncedSearchTerm, pageSize, currentPage, showError]);

  const fetchCategories = useCallback(async () => {
    if (!authToken || !selectedShopCode) return;
    try {
      const data = await fetchData(`api/pos/v1/category/all`, 'GET', null, authToken, selectedShopCode);
      setCategories(data.data || []);
    } catch (err) {
      setCategories([]);
    }
  }, [authToken, selectedShopCode]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  const fetchInventoryHistory = async (productId) => {
    if (!authToken || !selectedShopCode || !productId) return;
    setInventoryHistoryLoading(true);
    try {
      const data = await fetchData(`api/pos/v1/product/inventory/${productId}`, 'GET', null, authToken, selectedShopCode);
      setInventoryHistory(data.data || []);
    } catch (err) {
      showError('Failed to load inventory history.');
      setInventoryHistory([]);
    } finally {
      setInventoryHistoryLoading(false);
    }
  };

  const fetchProductSales = async (productId) => {
    if (!authToken || !selectedShopCode || !productId) return;
    setProductSalesLoading(true);
    try {
      const data = await fetchData(`api/pos/v1/order/product/${productId}`, 'GET', null, authToken, selectedShopCode);
      setProductSales(data.data || []);
    } catch (err) {
      showError('Failed to load sales history.');
      setProductSales([]);
    } finally {
      setProductSalesLoading(false);
    }
  };

  const handleAddProduct = () => {
    setCurrentProduct({
      name: '', description: '', categoryId: '', brand: '', barcode: '',
      threshold: 5, costPrice: 0, sellingPrice: 0, expiry: ''
    });
    setIsModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setCurrentProduct({
      ...product,
      categoryId: product.categoryId || (categories.find(c => c.name === product.categoryName)?.id || ''),
    });
    setIsModalOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await fetchData(`api/pos/v1/product/remove/${id}`, 'DELETE', null, authToken, selectedShopCode);
        showSuccess('Product removed successfully.');
        fetchProducts();
      } catch (err) {
        showError('Failed to delete product.');
      }
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (currentProduct?.id) {
        await fetchData(`api/pos/v1/product/update/${currentProduct.id}`, 'POST', productData, authToken, selectedShopCode);
        showSuccess('Product updated successfully.');
      } else {
        await fetchData(`api/pos/v1/product/create`, 'POST', productData, authToken, selectedShopCode);
        showSuccess('Product created successfully.');
      }
      setIsModalOpen(false);
      fetchProducts();
    } catch (err) {
      showError(`Failed to save product.`);
    }
  };

  const handleManageInventory = (productId) => {
    setInventoryProductId(productId);
    setInventoryQuantity(0);
    setInventoryNotes('');
    setInventoryType('add');
    setIsInventoryModalOpen(true);
  };

  const handleSaveInventory = async () => {
    if (inventoryQuantity <= 0) {
      showError('Please enter a quantity greater than 0.');
      return;
    }
    try {
      const endpoint = inventoryType === 'add' ? 'add' : 'remove';
      await fetchData(
        `api/pos/v1/product/inventory/${inventoryProductId}/${endpoint}`,
        'POST',
        { quantity: Number(inventoryQuantity), notes: inventoryNotes },
        authToken,
        selectedShopCode
      );
      showSuccess(`Stock ${inventoryType === 'add' ? 'added' : 'deducted'} successfully.`);
      setIsInventoryModalOpen(false);
      fetchProducts();
    } catch (err) {
      showError(`Failed to update inventory.`);
    }
  };

  const handleExport = async () => {
    if (!authToken || !selectedShopCode) return;
    setIsExporting(true);
    try {
      const params = {};
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      params.format = exportFormat;

      let filename = `products_${getCurrentDateISO()}.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
      await downloadFile('api/pos/v1/product/download', 'GET', params, authToken, selectedShopCode, filename);
      showSuccess(`Products exported as ${exportFormat.toUpperCase()}.`);
    } catch (err) {
      showError('Export failed.');
    } finally {
      setIsExporting(false);
    }
  };

  const tableHeaders = ['Product Details', 'Category & Brand', 'Barcode', 'Stock Status', 'Cost Price', 'Selling Price', 'Expiry', 'Actions'];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Product Catalog"
        subtitle="Search, filter, manage inventory levels, and configure pricing"
        breadcrumbs={[{ label: 'Products' }]}
        action={
          <Button
            onClick={handleAddProduct}
            icon={<Plus className="w-4 h-4" />}
            size="sm"
          >
            Add New Product
          </Button>
        }
      />

      {/* Filter and Control Bar */}
      <GlassmorphicCard noPadding>
        <div className="p-4 md:p-5 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
            <div className="w-full sm:w-80">
              <InputField
                placeholder="Search name, barcode, brand..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="w-4 h-4" />}
                className="!mb-0"
              />
            </div>
            <div className="w-full sm:w-36">
              <SelectField
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                options={[
                  { label: '10 per page', value: 10 },
                  { label: '25 per page', value: 25 },
                  { label: '50 per page', value: 50 },
                  { label: '100 per page', value: 100 },
                ]}
                className="!mb-0"
              />
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            <Button
              onClick={handleExport}
              disabled={isExporting}
              variant="light"
              size="sm"
              icon={<Download className="w-3.5 h-3.5" />}
            >
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </div>
      </GlassmorphicCard>

      {/* Products Table */}
      <Table
        headers={tableHeaders}
        data={products}
        loading={loading}
        emptyMessage={searchTerm ? 'No products match your search query.' : 'No products found in store.'}
        renderRow={(product) => {
          const isOutOfStock = product.quantity <= 0;
          const isLowStock = !isOutOfStock && product.quantity <= product.threshold;

          return (
            <tr
              key={product.id}
              className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors"
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200/60 dark:border-emerald-800/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                    <Package className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900 dark:text-white text-sm block">
                      {product.name}
                    </span>
                    {product.description && (
                      <span className="text-xs text-slate-400 dark:text-slate-500 truncate max-w-[180px] block">
                        {product.description}
                      </span>
                    )}
                  </div>
                </div>
              </td>

              <td className="px-6 py-4 whitespace-nowrap">
                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                  {product.categoryName || 'General'}
                </span>
                {product.brand && (
                  <span className="block text-[11px] text-slate-400 mt-0.5">
                    {product.brand}
                  </span>
                )}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-slate-600 dark:text-slate-300">
                {product.barcode || '—'}
              </td>

              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      isOutOfStock
                        ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 border border-rose-200 dark:border-rose-800'
                        : isLowStock
                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                        : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                    }`}
                  >
                    {isOutOfStock ? (
                      <XCircle className="w-3.5 h-3.5" />
                    ) : isLowStock ? (
                      <AlertCircle className="w-3.5 h-3.5" />
                    ) : (
                      <CheckCircle className="w-3.5 h-3.5" />
                    )}
                    {product.quantity} in stock
                  </span>
                  {product.threshold > 0 && (
                    <span className="text-[11px] text-slate-400">
                      (min: {product.threshold})
                    </span>
                  )}
                </div>
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600 dark:text-slate-300 font-medium">
                {formatCurrency(product.costPrice)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(product.sellingPrice)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
                {product.expiry ? formatDate(product.expiry) : '—'}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-medium">
                <Dropdown
                  align="right"
                  trigger={
                    <button className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  }
                >
                  <DropdownItem
                    onClick={() => handleEditProduct(product)}
                    icon={<Edit2 className="w-3.5 h-3.5" />}
                  >
                    Edit Product
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => handleManageInventory(product.id)}
                    icon={<PackagePlus className="w-3.5 h-3.5" />}
                  >
                    Adjust Stock
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      setSelectedProductForHistory(product);
                      setIsInventoryHistoryModalOpen(true);
                      fetchInventoryHistory(product.id);
                    }}
                    icon={<History className="w-3.5 h-3.5" />}
                  >
                    Stock Movement
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      setSelectedProductForSales(product);
                      setIsProductSalesModalOpen(true);
                      fetchProductSales(product.id);
                    }}
                    icon={<TrendingUp className="w-3.5 h-3.5" />}
                  >
                    Sales History
                  </DropdownItem>
                  <div className="my-1 border-t border-slate-100 dark:border-slate-700/80" />
                  <DropdownItem
                    onClick={() => handleDeleteProduct(product.id)}
                    icon={<Trash2 className="w-3.5 h-3.5" />}
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/30"
                  >
                    Delete Item
                  </DropdownItem>
                </Dropdown>
              </td>
            </tr>
          );
        }}
      />

      {/* Pagination Bar */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Showing <span className="font-semibold text-slate-900 dark:text-white">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
            <span className="font-semibold text-slate-900 dark:text-white">{Math.min(currentPage * pageSize, totalItems)}</span> of{' '}
            <span className="font-semibold text-slate-900 dark:text-white">{totalItems}</span> products
          </p>

          <div className="flex items-center gap-1.5">
            <Button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage <= 1}
              variant="light"
              size="sm"
            >
              Previous
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
                    currentPage === pageNum
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <Button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages}
              variant="light"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Modal: Create / Edit Product */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentProduct?.id ? 'Edit Product Catalog Entry' : 'Create New Product'}
        size="large"
      >
        <ProductForm
          product={currentProduct}
          onSave={handleSaveProduct}
          categories={categories}
        />
      </Modal>

      {/* Modal: Manage Inventory Stock */}
      <Modal
        isOpen={isInventoryModalOpen}
        onClose={() => setIsInventoryModalOpen(false)}
        title="Adjust Inventory Stock"
      >
        <div className="space-y-4">
          <div className="p-3.5 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/40 text-xs text-emerald-800 dark:text-emerald-200">
            Current In-Store Quantity:{' '}
            <span className="font-bold text-sm">
              {products.find(p => p.id === inventoryProductId)?.quantity || 0} units
            </span>
          </div>

          <SelectField
            label="Adjustment Type"
            value={inventoryType}
            onChange={(e) => setInventoryType(e.target.value)}
            options={[
              { label: 'Receive New Stock (Add +)', value: 'add' },
              { label: 'Shrinkage / Damage / Return (Deduct -)', value: 'remove' },
            ]}
          />

          <InputField
            label="Quantity Adjustment"
            type="number"
            value={inventoryQuantity}
            onChange={(e) => setInventoryQuantity(e.target.value)}
            placeholder="Enter quantity"
            min="1"
            required
          />

          <InputField
            label="Reason / Notes (Optional)"
            value={inventoryNotes}
            onChange={(e) => setInventoryNotes(e.target.value)}
            placeholder="e.g. Supplier PO #4029, Broken item write-off"
          />

          <div className="pt-2">
            <Button
              onClick={handleSaveInventory}
              className="w-full py-3"
              variant={inventoryType === 'add' ? 'primary' : 'danger'}
            >
              {inventoryType === 'add' ? 'Add Stock to Inventory' : 'Deduct Stock from Inventory'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: Inventory History */}
      <Modal
        isOpen={isInventoryHistoryModalOpen}
        onClose={() => setIsInventoryHistoryModalOpen(false)}
        title={`Stock Movement Logs • ${selectedProductForHistory?.name || ''}`}
        size="large"
      >
        {inventoryHistoryLoading ? (
          <div className="py-12 text-center text-slate-400">
            <div className="inline-flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              <span>Loading stock movement history...</span>
            </div>
          </div>
        ) : inventoryHistory.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            No stock movement history recorded for this item.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Timestamp</th>
                  <th className="px-4 py-3 text-left font-semibold">Mode</th>
                  <th className="px-4 py-3 text-left font-semibold">Qty Change</th>
                  <th className="px-4 py-3 text-left font-semibold">Balance</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {inventoryHistory.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-3 text-slate-500">{formatDateTime(item.createdAt)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${item.mode === 'increase' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'}`}>
                        {item.mode === 'increase' ? '+ Received' : '- Deducted'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold">{item.mode === 'increase' ? `+${item.quantity}` : `-${item.quantity}`}</td>
                    <td className="px-4 py-3 text-slate-400">{item.quantityOld} → <span className="font-semibold text-slate-800 dark:text-slate-100">{item.quantityNew}</span></td>
                    <td className="px-4 py-3 text-slate-500 truncate max-w-[200px]">{item.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Modal: Product Sales History */}
      <Modal
        isOpen={isProductSalesModalOpen}
        onClose={() => setIsProductSalesModalOpen(false)}
        title={`Order Sales History • ${selectedProductForSales?.name || ''}`}
        size="large"
      >
        {productSalesLoading ? (
          <div className="py-12 text-center text-slate-400">
            <div className="inline-flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              <span>Loading sales records...</span>
            </div>
          </div>
        ) : productSales.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            No sales recorded for this product yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Order #</th>
                  <th className="px-4 py-3 text-left font-semibold">Date</th>
                  <th className="px-4 py-3 text-left font-semibold">Qty Sold</th>
                  <th className="px-4 py-3 text-left font-semibold">Price</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {productSales.map((sale) => {
                  let orderItems = sale.orderItems;
                  if (typeof orderItems === 'string') {
                    try { orderItems = JSON.parse(orderItems); } catch (e) { orderItems = []; }
                  }
                  const item = Array.isArray(orderItems)
                    ? orderItems.find(i => i.productId === selectedProductForSales?.id || i.productName === selectedProductForSales?.name)
                    : null;

                  return (
                    <tr key={sale.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white">{sale.orderNumber}</td>
                      <td className="px-4 py-3 text-slate-500">{formatDateTime(sale.createdAt)}</td>
                      <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">{item?.quantity || 1}</td>
                      <td className="px-4 py-3 text-emerald-600 font-semibold">{formatCurrency(item?.sellingPrice || 0)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sale.status === 'PAID' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-slate-100 text-slate-700'}`}>
                          {sale.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          onClick={() => {
                            setSelectedSale(sale);
                            setIsSaleDetailsModalOpen(true);
                          }}
                          variant="light"
                          size="sm"
                          icon={<Eye className="w-3 h-3" />}
                        >
                          Invoice
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Modal: Sale Invoice Details */}
      <Modal
        isOpen={isSaleDetailsModalOpen}
        onClose={() => setIsSaleDetailsModalOpen(false)}
        title={`Order Details • ${selectedSale?.orderNumber || ''}`}
      >
        {selectedSale && (
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80">
              <div>
                <p className="text-slate-400">Order Number</p>
                <p className="font-bold text-slate-900 dark:text-white text-sm">{selectedSale.orderNumber}</p>
              </div>
              <div>
                <p className="text-slate-400">Status</p>
                <span className="px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300">
                  {selectedSale.status}
                </span>
              </div>
              <div>
                <p className="text-slate-400">Order Date</p>
                <p className="font-medium text-slate-700 dark:text-slate-200">{formatDateTime(selectedSale.createdAt)}</p>
              </div>
              <div>
                <p className="text-slate-400">Net Total</p>
                <p className="font-bold text-emerald-600 dark:text-emerald-400 text-sm">{formatCurrency(selectedSale.netAmount)}</p>
              </div>
            </div>

            {selectedSale.note && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200">
                <span className="font-semibold">Note:</span> {selectedSale.note}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductsPage;
